import { useCallback, useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import { PracticeVideoPart, PracticeVideos, YouTubeVideoId } from '../../../common/src/model.ts';
import {
  allPracticeVideoParts,
  parseYouTubeVideoId,
  practiceVideoCount,
  practiceVideoPartLabels,
  practiceVideoParts,
  youTubeThumbnailUrl,
  youTubeWatchUrl,
} from '../../../common/src/youtube.ts';
import CheckMarkIcon from '../assets/check-mark-icon.svg?react';
import ChevronDownIcon from '../assets/chevron-down.svg?react';
import PlusIcon from '../assets/plus.svg?react';
import { YouTubeMark } from '../player/practice_video/youtube_mark.tsx';

// The Practice videos dropdown of the edit-song pop-up (GitHub issue #4041): one YouTube link field per part, a
// thumbnail confirming each link, and an optional whole-choir video.

type PracticeVideoFields = Partial<Record<PracticeVideoPart, string>>;

// what the link field shows for a parsed video: the short link without the scheme
function shownLink(id: YouTubeVideoId): string {
  return youTubeWatchUrl(id).replace(/^https:\/\//, '');
}

// the videos the fields hold: the parts whose text parses as a YouTube link (empty and unparsable fields are dropped)
function practiceVideosFromFields(fields: PracticeVideoFields): PracticeVideos {
  const videos: PracticeVideos = {};
  for (const part of allPracticeVideoParts) {
    const id = parseYouTubeVideoId(fields[part] ?? '');
    if (id) {
      videos[part] = id;
    }
  }
  return videos;
}

export function isSamePracticeVideos(a: PracticeVideos | undefined, b: PracticeVideos | undefined): boolean {
  return allPracticeVideoParts.every((part) => a?.[part] === b?.[part]);
}

// the grey text beside "Practice videos" on the closed dropdown
function practiceVideoSummary(videos: PracticeVideos | undefined): string {
  const { parts, hasWholeSong } = practiceVideoCount(videos);
  if (parts === 0) {
    return hasWholeSong ? 'Whole choir' : 'None';
  }
  return `${parts} of ${practiceVideoParts.length} parts${hasWholeSong ? ' + whole choir' : ''}`;
}

/**
 * The link fields, which hold free text while the admin types, starting from the song's stored videos, and the
 * videos they hold: only the parts whose text parses are saved.
 */
export function usePracticeVideoFields(storedVideos: PracticeVideos | undefined): {
  videoFields: PracticeVideoFields;
  setVideoField: (part: PracticeVideoPart, text: string) => void;
  editedVideos: PracticeVideos;
} {
  const [videoFields, setVideoFields] = useState<PracticeVideoFields>(() =>
    Object.fromEntries(
      allPracticeVideoParts.flatMap((part) => {
        const id = storedVideos?.[part];
        return id ? [[part, shownLink(id)]] : [];
      }),
    ),
  );
  const setVideoField = useCallback((part: PracticeVideoPart, text: string) => {
    setVideoFields((fields) => ({ ...fields, [part]: text }));
  }, []);
  const editedVideos = useMemo(() => practiceVideosFromFields(videoFields), [videoFields]);
  return { videoFields, setVideoField, editedVideos };
}

export function PracticeVideosSection({
  videoFields,
  setVideoField,
  editedVideos,
}: {
  videoFields: PracticeVideoFields;
  setVideoField: (part: PracticeVideoPart, text: string) => void;
  editedVideos: PracticeVideos;
}) {
  const [hasWholeChoirRow, setHasWholeChoirRow] = useState(() => !!videoFields.all);
  const [isOpen, setIsOpen] = useState(false);
  const baseId = useId();
  const ids = { videoSection: `${baseId}-video-section`, videoInput: `${baseId}-video-input` };

  return (
    <VideosSection $isOpen={isOpen}>
      <Disclosure
        type="button"
        aria-expanded={isOpen}
        aria-controls={ids.videoSection}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
        $isOpen={isOpen}
      >
        <DisclosureTitle>
          <YouTubeMark width={18} height={13} />
          Practice videos
        </DisclosureTitle>
        <DisclosureSummary>
          <span>{practiceVideoSummary(editedVideos)}</span>
          <Chevron aria-hidden="true" $isOpen={isOpen} />
        </DisclosureSummary>
      </Disclosure>
      {isOpen && (
        <>
          <VideoRows id={ids.videoSection}>
            {allPracticeVideoParts
              .filter((part) => part !== 'all' || hasWholeChoirRow)
              .map((part) => (
                <PracticeVideoRow
                  key={part}
                  inputId={`${ids.videoInput}-${part}`}
                  label={practiceVideoPartLabels[part]}
                  text={videoFields[part] ?? ''}
                  onChangeText={(text) => setVideoField(part, text)}
                />
              ))}
          </VideoRows>
          {!hasWholeChoirRow && (
            <WholeChoirButton type="button" onClick={() => setHasWholeChoirRow(true)}>
              <Plus aria-hidden="true" />
              Whole-choir video
            </WholeChoirButton>
          )}
        </>
      )}
    </VideosSection>
  );
}

// One part's link field. The thumbnail appears once the text parses as a YouTube link, and the check once the
// thumbnail has loaded: YouTube serves a thumbnail only for a video that exists. The parsed id is saved either way.
function PracticeVideoRow({
  inputId,
  label,
  text,
  onChangeText,
}: {
  inputId: string;
  label: string;
  text: string;
  onChangeText: (text: string) => void;
}) {
  const videoId = parseYouTubeVideoId(text);
  const [loadedThumbnailId, setLoadedThumbnailId] = useState<YouTubeVideoId>();
  // the thumbnail of a video id that does not exist (or cannot load offline) fails; show the empty slot then
  const [failedThumbnailId, setFailedThumbnailId] = useState<YouTubeVideoId>();
  const showsThumbnail = !!videoId && videoId !== failedThumbnailId;
  const isChecked = showsThumbnail && videoId === loadedThumbnailId;
  return (
    <VideoRow $isPending={!videoId} data-part-row={label}>
      <VideoLabel htmlFor={inputId}>{label}</VideoLabel>
      {showsThumbnail ? (
        <Thumbnail
          src={youTubeThumbnailUrl(videoId)}
          alt=""
          onLoad={() => setLoadedThumbnailId(videoId)}
          onError={() => setFailedThumbnailId(videoId)}
        />
      ) : (
        <ThumbnailPlaceholder />
      )}
      {/* text, not url: the field shows the schemeless youtu.be/<id>, which a url field calls invalid */}
      <VideoLinkInput
        id={inputId}
        type="text"
        inputMode="url"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder="Paste a link"
        value={text}
        onChange={(event) => onChangeText(event.target.value)}
        onBlur={() => videoId && onChangeText(shownLink(videoId))}
      />
      {isChecked && <Check role="img" aria-label="Link checked" />}
    </VideoRow>
  );
}

const VideosSection = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $isOpen }) =>
    $isOpen ? '' : 'border-top: 1px solid var(--color-row-divider); border-bottom: 1px solid var(--color-row-divider);'}
`;

const Disclosure = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${({ $isOpen }) => ($isOpen ? 36 : 48)}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--color-text);
  font-family: Jost-Regular, Arial, sans-serif;
  cursor: pointer;
`;

const DisclosureTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const DisclosureSummary = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--color-text-label);
`;

const Chevron = styled(ChevronDownIcon)<{ $isOpen: boolean }>`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin: 3px;
  fill: currentColor;
  transform: rotate(${({ $isOpen }) => ($isOpen ? 180 : 0)}deg);
`;

const VideoRows = styled.div`
  border: 1.5px solid var(--color-field-border);
  border-radius: 12px;
  overflow: hidden;
`;

const VideoRow = styled.div<{ $isPending: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 10px;
  background: ${({ $isPending }) => ($isPending ? 'var(--color-row-pending)' : 'transparent')};

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-row-divider);
  }
`;

const VideoLabel = styled.label`
  flex-shrink: 0;
  width: 54px;
  font-size: 13px;
`;

// the placeholder's color until the picture arrives
const Thumbnail = styled.img`
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  background: var(--color-thumbnail-placeholder);
`;

const ThumbnailPlaceholder = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 4px;
  background: var(--color-thumbnail-placeholder);
`;

const VideoLinkInput = styled.input`
  flex-grow: 1;
  min-width: 0;
  padding: 0;
  border: 0;
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 13px;
  color: var(--color-text-secondary);
  background: transparent;
  outline: none;
`;

const Check = styled(CheckMarkIcon)`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin: 2px;
  fill: var(--color-link-checked);
`;

const WholeChoirButton = styled.button`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 2px;
  border: 0;
  background: transparent;
  color: var(--color-nightingale);
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

// the asset's path has its own black fill, which only a rule on the path overrides
const Plus = styled(PlusIcon)`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin: 1.5px;

  path {
    fill: currentColor;
  }
`;
